/**
 * Pure derivations for the realtor Home dashboard: the date windows it asks
 * the schedule endpoint for, the counts it shows, and the hot-listings rank.
 */

import type {
	LeadWithLinks,
	ScheduledActivity,
} from "@/features/realtor/services/leads-api.service";
import type { Listing } from "@/features/realtor/services/listings-api.service";

const DAY_MS = 24 * 60 * 60 * 1000;

export interface DashboardRanges {
	now: Date;
	/** Local midnight at the start of today. */
	todayStart: Date;
	/** Local midnight at the start of tomorrow (exclusive end of today). */
	tomorrowStart: Date;
	/** `now` plus 7 × 24 hours. */
	weekEnd: Date;
}

/**
 * Built from calendar fields rather than `+ 24h`, so the end of today is the
 * next local midnight even on a day with a DST shift.
 */
export function dashboardRanges(now: Date): DashboardRanges {
	const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
	const tomorrowStart = new Date(
		now.getFullYear(),
		now.getMonth(),
		now.getDate() + 1,
	);
	return {
		now,
		todayStart,
		tomorrowStart,
		weekEnd: new Date(now.getTime() + 7 * DAY_MS),
	};
}

function scheduledMs(item: ScheduledActivity): number {
	// The endpoint only returns rows with a scheduled_at.
	return new Date(item.activity.scheduledAt as string).getTime();
}

/** Splits "everything open before tomorrow" into overdue and today. */
export function splitDue(
	items: ScheduledActivity[],
	todayStart: Date,
): { overdue: ScheduledActivity[]; today: ScheduledActivity[] } {
	const cutoff = todayStart.getTime();
	const sorted = [...items].sort((a, b) => scheduledMs(a) - scheduledMs(b));
	return {
		overdue: sorted.filter((i) => scheduledMs(i) < cutoff),
		today: sorted.filter((i) => scheduledMs(i) >= cutoff),
	};
}

/** Reminders and calls among the open items due before tomorrow. */
export function countFollowUpsDue(dueItems: ScheduledActivity[]): number {
	return dueItems.filter(
		(i) => i.activity.type === "reminder" || i.activity.type === "call",
	).length;
}

export function countVisits(items: ScheduledActivity[]): number {
	return items.filter((i) => i.activity.type === "viewing").length;
}

/** Items at or after `from`, earliest first, at most `limit`. */
export function itemsFrom(
	items: ScheduledActivity[],
	from: Date,
	limit: number,
): ScheduledActivity[] {
	return items
		.filter((i) => scheduledMs(i) >= from.getTime())
		.sort((a, b) => scheduledMs(a) - scheduledMs(b))
		.slice(0, limit);
}

export const isOpenLead = (lead: LeadWithLinks): boolean =>
	lead.lead.pipelineStage !== "closed_won" &&
	lead.lead.pipelineStage !== "closed_lost";

export interface HotListing {
	listing: Listing;
	/** Open (not closed won/lost) leads linked to this listing. */
	openLeadCount: number;
}

/**
 * `active` listings ranked by how many open leads are linked to them, then by
 * most recently updated, capped at `limit`.
 */
export function hotListings(
	listings: Listing[],
	leads: LeadWithLinks[],
	limit = 4,
): HotListing[] {
	const counts = new Map<string, number>();
	for (const lead of leads) {
		if (!isOpenLead(lead)) continue;
		for (const linked of lead.listings) {
			counts.set(linked.id, (counts.get(linked.id) ?? 0) + 1);
		}
	}
	return listings
		.filter((l) => l.status === "active")
		.map((listing) => ({
			listing,
			openLeadCount: counts.get(listing.id) ?? 0,
		}))
		.sort(
			(a, b) =>
				b.openLeadCount - a.openLeadCount ||
				new Date(b.listing.updatedAt).getTime() -
					new Date(a.listing.updatedAt).getTime(),
		)
		.slice(0, limit);
}
